import axios, { AxiosError } from 'axios';
import runtimeConfig from '../runtime-config.json';
import { useAuthStore } from '../store/auth';
import { useBranchStore } from '../store/branch';
import toast from 'react-hot-toast';

/**
 * Build API base URL at runtime.
 *
 * Strategy:
 *   - Port comes from /src/runtime-config.json, which the backend writes on startup.
 *   - Host comes from window.location.hostname so the same build works on
 *     localhost AND on http://23.19.56.232 (LAN) without rebuilding.
 */
function buildApiBaseUrl(): string {
  // Prefer VITE_API_URL (set in production .env to e.g. /omniretail/api)
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  // Dev fallback: read port from runtime-config + use window.location.hostname
  const port = runtimeConfig.apiPort ?? 5001;
  const host = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
  return `http://${host}:${port}/api`;
}

export const apiBaseUrl = buildApiBaseUrl();

export const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach access token on every request
api.interceptors.request.use((cfg) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  // Attach the selected branch (omit when "All branches" so backend stays unscoped)
  const branchId = useBranchStore.getState().branchId;
  if (branchId) {
    cfg.headers['X-Branch-Id'] = branchId;
  }
  return cfg;
});

// Attempt silent refresh on 401, queue concurrent requests
let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) return Promise.reject(error);
    const status = error.response.status;
    const originalReq = error.config as any;

    if (status === 401 && !originalReq._retry) {
      originalReq._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = (async () => {
            const refreshToken = useAuthStore.getState().refreshToken;
            if (!refreshToken) return null;
            try {
              const res = await axios.post(`${apiBaseUrl}/auth/refresh`, { refreshToken });
              const { accessToken, refreshToken: newRefresh } = res.data;
              useAuthStore.getState().setTokens(accessToken, newRefresh);
              return accessToken;
            } catch {
              useAuthStore.getState().clear();
              return null;
            }
          })();
        }
        const newToken = await refreshPromise;
        refreshPromise = null;
        if (newToken) {
          originalReq.headers.Authorization = `Bearer ${newToken}`;
          return api.request(originalReq);
        }
      } catch {
        refreshPromise = null;
      }
    }

    // Graceful handling for plan/feature gates: surface a clear notice instead of a
    // dead page when the backend blocks a module by subscription plan or feature flag.
    if (status === 403) {
      const code = (error.response.data as any)?.error?.code;
      if (code === 'PLAN_UPGRADE_REQUIRED' || code === 'FEATURE_DISABLED') {
        const msg = (error.response.data as any)?.error?.message
          ?? 'This feature is not available on your current plan.';
        toast.error(msg, { id: 'plan-gate' });
      }
    }

    return Promise.reject(error);
  }
);

export function getErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    return (
      (err.response?.data as any)?.error?.message ??
      err.message ??
      'Request failed'
    );
  }
  return String(err);
}
